import React from "react"

import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Collapse,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import HighlightOffIcon from "@mui/icons-material/HighlightOff"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import SendIcon from "@mui/icons-material/Send"
import {
    t,
    Trans,
} from "@lingui/macro"

import {
    AssetMatchBrand,
    AssetMatchesBrandStatus,
} from "models"

interface AssetBrandMatchingSectionProps {
    filteredMatches: AssetMatchBrand[]
    sendableMatchIds: string[]
    allMatchesSelected: boolean
    matchSearch: string
    selectedMatchIds: string[]
    expandedMatchIds: string[]
    onToggleSelectAll: (matchIds: string[]) => void
    onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onToggleMatchSelection: (matchId: string) => void
    onToggleMatchExpanded: (matchId: string) => void
    onOpenSingleMatchProposal: (match: AssetMatchBrand) => void
    onNavigateToBrand: (brandId: string) => void
    renderCompatibilityEmoticon: (compatibility: number) => React.ReactNode
    renderCompatibilityBar: (compatibility: number) => React.ReactNode
}

export const AssetBrandMatchingSection = ({
    filteredMatches,
    sendableMatchIds,
    allMatchesSelected,
    matchSearch,
    selectedMatchIds,
    expandedMatchIds,
    onToggleSelectAll,
    onSearchChange,
    onToggleMatchSelection,
    onToggleMatchExpanded,
    onOpenSingleMatchProposal,
    onNavigateToBrand,
    renderCompatibilityEmoticon,
    renderCompatibilityBar,
}: AssetBrandMatchingSectionProps) => {
    const visibleMatchesWithoutScroll = 5
    const matchRowHeight = 120
    const matchListMaxHeight = visibleMatchesWithoutScroll * matchRowHeight

    return (
        <Box sx={{ borderRadius: 2, overflow: "hidden", border: 1, borderColor: "divider", backgroundColor: "background.paper" }}>
            <Box sx={{ backgroundColor: "#3f7c7b", color: "white", px: 3, py: 2 }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            <Trans id="asset-details.brand-matching.title">Brand Matching</Trans>
                        </Typography>
                        <Typography variant="body2">
                            <Trans id="asset-details.brand-matching.count">
                                {filteredMatches.length} brand compatibili identificati dall'AI
                            </Trans>
                        </Typography>
                    </Box>
                    {sendableMatchIds.length > 0 && (
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => onToggleSelectAll(sendableMatchIds)}
                            sx={{ textTransform: "none" }}
                        >
                            {allMatchesSelected ? (
                                <Trans id="asset-details.brand-matching.deselect-all">Deseleziona tutti</Trans>
                            ) : (
                                <Trans id="asset-details.brand-matching.select-all">Seleziona tutti</Trans>
                            )}
                        </Button>
                    )}
                </Stack>
            </Box>
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: "divider" }}>
                <TextField
                    fullWidth
                    placeholder={t({ id: "asset-details.brand-matching.search", message: "Cerca Brand" })}
                    value={matchSearch}
                    onChange={onSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <ExpandMoreIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            <Box>
                <Grid container sx={{ px: 2, py: 1 }} alignItems="center">
                    <Grid item xs={6} md={5}>
                        <Typography variant="caption" color="text.secondary">
                            <Trans id="asset-details.brand-matching.brand">Brand</Trans>
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <Typography variant="caption" color="text.secondary">
                            <Trans id="asset-details.brand-matching.compatibility">Compatibilità</Trans>
                        </Typography>
                    </Grid>
                </Grid>
                <Divider />
                {filteredMatches.length === 0 ? (
                    <Typography sx={{ px: 2, py: 3 }} color="text.secondary">
                        <Trans id="asset-details.brand-matching.empty">Nessun brand trovato.</Trans>
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            maxHeight: matchListMaxHeight,
                            overflowY: "auto",
                        }}
                    >
                        {filteredMatches.map((match) => {
                            const isExpanded = expandedMatchIds.includes(match.id)
                            const canSendProposal = match.status !== AssetMatchesBrandStatus.SENT
                            const isSelected = canSendProposal && selectedMatchIds.includes(match.id)
                            const compatibilityValue = match.compatibility ?? 0
                            const positivePoints = (match.positivePoints ?? [])
                                .filter((point) => point && point.trim() !== "")
                            const negativePoints = (match.negativePoints ?? [])
                                .filter((point) => point && point.trim() !== "")
                            return (
                                <Box key={match.id} sx={{ px: 2, py: 2, borderBottom: 1, borderColor: "divider" }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={5}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => canSendProposal && onToggleMatchSelection(match.id)}
                                                    disabled={!canSendProposal}
                                                    size="small"
                                                />
                                            <Avatar src={match.brand?.logo ?? ""} sx={{ width: 36, height: 36 }}>
                                                {match.brand?.name?.charAt(0) ?? "B"}
                                            </Avatar>
                                                <Typography fontWeight={600}>
                                                    {match.brand?.name ?? "-"}
                                                </Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Stack spacing={1}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography fontWeight={600}>
                                                        {compatibilityValue}%
                                                    </Typography>
                                                {renderCompatibilityEmoticon(compatibilityValue)}
                                            </Stack>
                                            {renderCompatibilityBar(compatibilityValue)}
                                        </Stack>
                                    </Grid>
                                        <Grid item xs={12} md={1}>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                                            <Typography variant="caption" color="text.secondary">
                                                <Trans id="asset-details.brand-matching.details">Dettagli match</Trans>
                                            </Typography>
                                            <IconButton size="small" onClick={() => onToggleMatchExpanded(match.id)}>
                                                <ExpandMoreIcon
                                                    sx={{
                                                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                                        transition: "transform 0.2s ease",
                                                    }}
                                                />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 2, pl: { xs: 0, md: 6 } }}>
                                        <Typography fontWeight={600} sx={{ mb: 1 }}>
                                            <Trans id="asset-details.brand-matching.why">Perché questo match?</Trans>
                                        </Typography>
                                        <Stack spacing={1}>
                                            {positivePoints.length === 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <Trans id="asset-details.brand-matching.why.empty">Nessun punto disponibile.</Trans>
                                                </Typography>
                                            )}
                                            {positivePoints.map((point, index) => (
                                                <Stack key={`${match.id}-positive-point-${index}`} direction="row" spacing={1} alignItems="center">
                                                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {point}
                                                    </Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                        <Typography fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                                            <Trans id="asset-details.brand-matching.negative">Punti a sfavore</Trans>
                                        </Typography>
                                        <Stack spacing={1}>
                                            {negativePoints.length === 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <Trans id="asset-details.brand-matching.negative.empty">Nessun punto disponibile.</Trans>
                                                </Typography>
                                            )}
                                            {negativePoints.map((point, index) => (
                                                <Stack key={`${match.id}-negative-point-${index}`} direction="row" spacing={1} alignItems="center">
                                                    <HighlightOffIcon fontSize="small" color="error" />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {point}
                                                    </Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                                            {canSendProposal && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="secondary"
                                                    startIcon={<SendIcon />}
                                                    onClick={() => onOpenSingleMatchProposal(match)}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    <Trans id="asset-details.brand-matching.send-single">Invia proposta singola</Trans>
                                                </Button>
                                            )}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => match.brand?.id && onNavigateToBrand(match.brand.id)}
                                                disabled={!match.brand?.id}
                                                sx={{ textTransform: "none" }}
                                            >
                                                <Trans id="asset-details.brand-matching.view-brand">Vedi Profilo Brand</Trans>
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Collapse>
                            </Box>
                            )
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
